import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { getMediaTypeImage } from '../../../../utils/GetMediaType';
import { ChatEnum } from '../../Chat.enum';
import { useChatContext } from '../../Chat.hook';
import {
  tChatAttachment,
  tInteractiveOutbound,
} from '../../Chat.types';
import { MessageBarContext } from './MessageBar.context';
import { tWabTemplatesVariablesAction } from './components/WabTemplatesVariablesAction/WabTemplatesVariablesAction.types.ts';
import {
  tComposerSubmitAdapter,
  tMessageBarContext,
} from './MessageBar.types.ts';
import { tErrorEntry } from 'src/utils/ErrorDirectory.types';
import { tTemplates } from 'src/pages/Pipe/Pipe.types.ts';
import { useDraftMessagesState } from './draftMessages.store';
import { useBlobUrlHolder } from 'src/hooks/useBlobUrlHolder.hook';

// Las acciones que TOMAN PRESTADO el composer para su propio texto: montan
// `LegacyInput` (o su propio cuerpo) y lo que escriben ahí no es el mensaje del
// chat. Ver BUG-1134.
const COMPOSER_BORROWING_ACTIONS = new Set<ChatEnum>([
  ChatEnum.PROGRAMMED_MESSAGES,
  ChatEnum.NOTES,
  ChatEnum.CREATE_QUICK_ACTION,
  ChatEnum.INTERACTIVE,
]);

export const useMessageBarContext = (): tMessageBarContext =>
  useContext(MessageBarContext);

export default function useMessageBar(): tMessageBarContext {
  const {
    quickActionState,
    selectedFollowUp,
    selectedNote,
    setSelectedNote,
    setSelectedFollowUp,
    setQuickActionState,
    droppedAttachments,
    setDroppedAttachments,
    setReplyMessage,
    selectedChat,
    textAreaRef,
  } = useChatContext();

  const isSendingRef = useRef(false);
  const quickReplyTriggerRef = useRef<number | null>(null);
  const [messageBarValue, setComposerValue] = useState('');
  const [draftMessages, setDraftMessages] = useDraftMessagesState();

  const selectedLeadId = selectedChat?.leadId;

  const [chatAttachments, setChatAttachments] = useState<tChatAttachment[]>(() =>
    draftMessages.find((draft) => draft.leadId === selectedChat?.leadId)?.attachments ?? [],
  );
  const attachmentUrls = useMemo(() => chatAttachments.map((attachment) => attachment.src), [chatAttachments]);
  useBlobUrlHolder(attachmentUrls);
  const [selectedAction, setSelectedAction] = useState<ChatEnum>(ChatEnum.CHAT);

  // BUG-1157: al cerrar las respuestas rápidas se olvida la `/` que las abrió,
  // para que abrirlas después con el botón no borre texto de la caja.
  useEffect(() => {
    if (selectedAction !== ChatEnum.QUICK_ACTIONS) {
      quickReplyTriggerRef.current = null;
    }
  }, [selectedAction]);

  // BUG-1134: el borrador persistido es un espejo del COMPOSER, no del teclado.
  //
  // Antes lo escribía únicamente `handleKeyUp` del TextAreaContainer, así que
  // todo lo que llenara el composer sin pasar por una tecla — el selector de
  // emojis, una respuesta rápida, una sugerencia de IA, pegar con el menú
  // contextual — dejaba el borrador desactualizado. Al enviar,
  // `settleDraftAttachmentSend` comparaba el borrador contra lo que se mandó y,
  // al no coincidir, lo conservaba a propósito: el texto ya enviado reaparecía.
  //
  // Va aquí y no en un `useEffect` espejo porque el efecto tiene una carrera al
  // cambiar de lead: el efecto de restauración de abajo escribe el composer
  // DESDE el borrador del lead nuevo, y un espejo con dependencias
  // `[messageBarValue, leadId]` correría en ese mismo commit con el leadId
  // nuevo y el valor viejo, archivando el texto del lead anterior bajo el
  // siguiente. Envolviendo el setter, el lead queda capturado en el momento de
  // la llamada, que es siempre el que el vendedor tiene en pantalla.
  const setMessageBarValue = useCallback((value: string) => {
    setComposerValue(value);
    // El composer es un espacio COMPARTIDO: Notas, Mensajes Programados, Crear
    // Respuesta Rápida y el compositor interactivo lo toman prestado para su
    // propio texto (son las que montan `LegacyInput`). Ese texto no es el
    // mensaje del chat, así que ni al cargarse ni al cancelarse debe pisar el
    // borrador del lead. `QUICK_ACTIONS` NO entra aquí: la respuesta rápida
    // escribe texto del chat, y además fija la acción de vuelta a `CHAT`
    // DESPUÉS de escribir el valor, así que excluirla se saltaría justo el caso
    // que BUG-1134 vino a arreglar.
    if (COMPOSER_BORROWING_ACTIONS.has(selectedAction) || !selectedLeadId) return;
    // Un composer de puros espacios no es un borrador: `handleKeyUp` guardaba
    // `value.trim()` y perder eso dejaba una fila que habilita el botón de
    // enviar sin nada que mandar.
    const message = value.trim() ? value : '';
    setDraftMessages((drafts) => {
      const next = drafts ?? [];
      const existing = next.find((draft) => draft.leadId === selectedLeadId);
      if (!existing) {
        return message ? [...next, { leadId: selectedLeadId, message, attachments: [] }] : next;
      }
      if (existing.message === message) return next;
      return next.map((draft) =>
        draft === existing ? { ...draft, message } : draft,
      );
    });
  }, [selectedAction, selectedLeadId, setDraftMessages]);

  // Vacía el composer SIN tocar el borrador. Para el `catch` de los dos caminos
  // de envío: RET-2232 quiere la caja limpia tras un fallo, pero el texto es lo
  // único que le queda al vendedor cuando el envío no salió, así que tiene que
  // seguir recuperable al reabrir el chat.
  const clearComposerKeepingDraft = useCallback(() => setComposerValue(''), []);
  const [wapiVariables, setWapiVariables] = useState<
    undefined | tWabTemplatesVariablesAction
  >(undefined);
  const [lastSuggestedResponse, setLastSuggestedResponse] = useState<
    { text: string; tone: string } | undefined
  >(undefined);
  const [inputBlockError, setInputBlockError] = useState<tErrorEntry | null>(null);
  const [scheduledWapiTemplate, setScheduledWapiTemplate] = useState<tTemplates | undefined>(undefined);
  const [isSchedulePickerMode, setIsSchedulePickerMode] = useState(false);
  const [interactiveDraft, setInteractiveDraft] = useState<
    tInteractiveOutbound | undefined
  >(undefined);
  const [composerSubmitAdapter, setComposerSubmitAdapter] =
    useState<tComposerSubmitAdapter | null>(null);

  useEffect(() => {
    setInputBlockError(null);
    setScheduledWapiTemplate(undefined);
    setIsSchedulePickerMode(false);
    // BUG-1157: la `/` guardada era del texto del lead anterior.
    quickReplyTriggerRef.current = null;

    if (draftMessages?.length && selectedChat?.leadId) {
      setSelectedAction(ChatEnum.CHAT);
      const currentDraft = draftMessages.findIndex(
        (draft) => draft.leadId === selectedChat?.leadId,
      );
      const message = draftMessages[currentDraft]?.message;
      const attachments = draftMessages[currentDraft]?.attachments;

      if (currentDraft >= 0) {
        setMessageBarValue(message);
        setChatAttachments(attachments || []);
      } else {
        setMessageBarValue('');
        setChatAttachments([]);
      }
    }
  }, [selectedChat?.leadId]);

  useEffect(() => {
    if (quickActionState.id) {
      setSelectedAction(ChatEnum.CREATE_QUICK_ACTION);
    }

    if (selectedFollowUp?.id) {
      setSelectedAction(ChatEnum.PROGRAMMED_MESSAGES);
    }

    if (selectedNote?.id) {
      setSelectedAction(ChatEnum.NOTES);
    }
  }, [quickActionState, selectedFollowUp, selectedNote]);

  // Fold finished uploads into the composer tray — but only the ones that belong
  // to the lead currently on screen. A drop or paste can stay in flight for tens
  // of seconds (S3 upload, plus a multi-second wasm decode for iPhone HEIC), and
  // if the agent switches leads in that window the attachment used to land in
  // whoever's tray was open. The next ordinary send then delivered it to the
  // wrong person. Attachments for other leads stay parked here until that lead
  // is selected again, which is why selectedChat.leadId is a dependency.
  useEffect(() => {
    if (!droppedAttachments.length) return;

    const currentLeadId = selectedChat?.leadId;
    // An attachment with no leadId predates this scoping; keep the old behavior.
    const belongsToCurrentLead = (attachment: tChatAttachment) =>
      !attachment.leadId || attachment.leadId === currentLeadId;

    const forCurrentLead = droppedAttachments.filter(belongsToCurrentLead);
    if (!forCurrentLead.length) return;

    setChatAttachments((prevAttachments) => {
      const existing = new Set(prevAttachments.map((attachment) => attachment.src));
      return [...prevAttachments, ...forCurrentLead.filter((attachment) =>
        !attachment.src?.startsWith('blob:') || !existing.has(attachment.src),
      )];
    });
    setDroppedAttachments((prevDropped) =>
      prevDropped.filter((attachment) => !belongsToCurrentLead(attachment)),
    );
  }, [droppedAttachments, selectedChat?.leadId]);

  useEffect(() => {
    if (!selectedChat?.leadId) return;

    const nextAttachments = chatAttachments ? chatAttachments : [];
    const nextDraftMessages = draftMessages || [];

    const currentDraft = draftMessages?.findIndex(
      (draft) => draft.leadId === selectedChat?.leadId,
    );

    const currentDraftExist = currentDraft !== undefined && currentDraft > -1;

    if (currentDraftExist) {
      nextDraftMessages[currentDraft].attachments = nextAttachments;
    } else {
      nextDraftMessages.push({
        leadId: selectedChat?.leadId || '',
        message: '',
        attachments: nextAttachments,
      });
    }

    setDraftMessages(nextDraftMessages);
    // BUG-1134: NO agregar `selectedChat?.leadId` aquí. Se intentó, para que un
    // leadId que resuelve tarde alcanzara a sembrar su fila, y abre una carrera
    // peor: en el commit del cambio de lead este efecto corre con el leadId
    // NUEVO y un `chatAttachments` que todavía es el del lead anterior, así que
    // archiva los adjuntos del lead A bajo el lead B. Lo cubre
    // `SendAction.blobUrls.test.tsx` ("keeps drafts added during a send").
    // La fila ya no hace falta para limpiar el borrador: el envío llama a
    // `settleDraftAttachmentSend` sin condicionarlo a que exista.
  }, [chatAttachments.length]);

  function passDataToLegacyInput(action: ChatEnum) {
    setSelectedAction(action);
    setReplyMessage(null);

    if (action === ChatEnum.CREATE_QUICK_ACTION) {
      setQuickActionState({
        id: '',
        isPublic: false,
        title: '',
        text: textAreaRef.current?.value || '',
        attachments:
          chatAttachments.map((file) => ({
            mediaType: getMediaTypeImage(file.type),
            mimeType: file.type,
            url: file.src || null,
            filename: file.name,
            vcard: null,
            decryption: null,
            duration: null,
            latitude: null,
            longitude: null,
          })) || undefined,
      });
    }

    if (action === ChatEnum.PROGRAMMED_MESSAGES) {
      setSelectedFollowUp({
        id: '',
        trigger_date: new Date(Date.now()),
        consumed: false,
        content: {
          text: textAreaRef.current?.value || '',
          attachments:
            chatAttachments.map((file) => ({
              mediaType: getMediaTypeImage(file.type),
              mimeType: file.type,
              url: file.src || null,
              filename: file.name,
              vcard: null,
              decryption: null,
              duration: null,
              latitude: null,
              longitude: null,
            })) || undefined,
        },
      });
    }

    if (action === ChatEnum.NOTES) {
      setSelectedNote({
        id: '',
        title: '',
        content: textAreaRef.current?.value || '',
        attachments:
          chatAttachments.map((file) => ({
            mediaType: getMediaTypeImage(file.type),
            mimeType: file.type,
            url: file.src || null,
            filename: file.name,
            vcard: null,
            decryption: null,
            duration: null,
            latitude: null,
            longitude: null,
          })) || undefined,
      });
    }
  }

  return {
    messageBarValue,
    setMessageBarValue,
    clearComposerKeepingDraft,
    selectedAction,
    setSelectedAction,
    chatAttachments,
    setChatAttachments,
    passDataToLegacyInput,
    draftMessages,
    setDraftMessages,
    wapiVariables,
    setWapiVariables,
    lastSuggestedResponse,
    setLastSuggestedResponse,
    isSendingRef,
    quickReplyTriggerRef,
    inputBlockError,
    setInputBlockError,
    scheduledWapiTemplate,
    setScheduledWapiTemplate,
    isSchedulePickerMode,
    setIsSchedulePickerMode,
    interactiveDraft,
    setInteractiveDraft,
    composerSubmitAdapter,
    setComposerSubmitAdapter,
  };
}
